import { useState } from 'react';
import { Mail, Lock, User, Sparkles, CheckCircle2, Building, Eye, EyeOff, AlertTriangle, ShieldCheck, Check, X, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../hooks/useBranding';
import { useLogin } from '../hooks/useLogin';
import { useRegister } from '../hooks/useRegister';
import {
  loginSchema,
  LoginFormData,
  registerSchema,
  RegisterFormData,
} from '../schemas/auth.schema';
import { parseApiErrorMessage } from '../services/api/api';

export default function LoginView({
  onLogin,
  onNavigate,
}: {
  onLogin: () => void;
  onNavigate?: (view: any) => void;
}) {
  const { loginWithGoogle } = useAuth();
  const { logoUrl, logoSize, iconUrl, iconSize, appName, loginHeroUrl } = useBranding();
  const brandImageUrl = logoUrl || iconUrl;

  const [isSignUp, setIsSignUp] = useState(false);
  const [serverError, setServerError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const loginMutation = useLogin();
  const registerMutation = useRegister();

  // Login Form
  const loginForm = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    mode: 'onChange',
    defaultValues: {
      email: '',
      password: '',
    },
  });

  // Register Form
  const registerForm = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    mode: 'onChange',
    defaultValues: {
      fullName: '',
      organizationName: '',
      email: '',
      password: '',
      confirmPassword: '',
      acceptTerms: false,
    },
  });

  const onLoginSubmit = async (data: LoginFormData) => {
    setServerError('');
    try {
      await loginMutation.mutateAsync({
        email: data.email,
        password: data.password,
        organizationId: data.organizationId,
      });
      onLogin();
    } catch (err: any) {
      const msg = parseApiErrorMessage(err);
      setServerError(msg);
    }
  };

  const onRegisterSubmit = async (data: RegisterFormData) => {
    setServerError('');
    try {
      const res = await registerMutation.mutateAsync({
        email: data.email,
        password: data.password,
        fullName: data.fullName,
        organizationName: data.organizationName,
      });
      if (res.accessToken) {
        onLogin();
      } else {
        setIsSignUp(false);
        loginForm.setValue('email', data.email);
      }
    } catch (err: any) {
      const msg = parseApiErrorMessage(err);
      setServerError(msg);
    }
  };

  const handleToggleMode = () => {
    setServerError('');
    setShowPassword(false);
    setShowConfirmPassword(false);
    setIsSignUp((prev) => !prev);
  };

  const isLoading = loginMutation.isPending || registerMutation.isPending;

  // Password Strength Calculation
  const regPasswordValue = registerForm.watch('password') || '';
  const pwdReqs = [
    { label: 'Pelo menos 8 caracteres', pass: regPasswordValue.length >= 8 },
    { label: 'Letras maiúsculas e minúsculas', pass: /[A-Z]/.test(regPasswordValue) && /[a-z]/.test(regPasswordValue) },
    { label: 'Pelo menos 1 número', pass: /[0-9]/.test(regPasswordValue) },
    { label: 'Pelo menos 1 caractere especial (!@#$...)', pass: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(regPasswordValue) },
  ];
  const passedCount = pwdReqs.filter((r) => r.pass).length;

  const getStrengthLabel = () => {
    if (!regPasswordValue) return { text: '', color: 'bg-neutral-200' };
    if (passedCount <= 1) return { text: 'Fraca', color: 'bg-red-500', textColor: 'text-red-600' };
    if (passedCount === 2) return { text: 'Moderada', color: 'bg-amber-500', textColor: 'text-amber-600' };
    if (passedCount === 3) return { text: 'Forte', color: 'bg-emerald-500', textColor: 'text-emerald-600' };
    return { text: 'Excelente', color: 'bg-indigo-600', textColor: 'text-indigo-600' };
  };

  const strength = getStrengthLabel();

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-[#FAFAFA] font-sans">
      {/* Left Side - Application Info */}
      <div className="hidden md:flex md:w-1/2 bg-[#111111] text-white p-12 lg:p-20 flex-col justify-between relative overflow-hidden">
        {/* Background Image with Premium Filter */}
        <div className="absolute inset-0 z-0">
          <img
            src={loginHeroUrl}
            alt="Hero Background"
            className="w-full h-full object-cover opacity-20 grayscale brightness-50 contrast-125"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-[#111111] via-[#111111]/70 to-transparent"></div>
        </div>

        <div className="relative z-10">
          <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center mb-8 backdrop-blur-sm border border-white/10 overflow-hidden">
            {brandImageUrl ? (
              <img
                src={brandImageUrl}
                alt={appName}
                style={{ maxWidth: `${logoUrl ? logoSize : iconSize}px`, maxHeight: `${logoUrl ? logoSize : iconSize}px` }}
                className="object-contain max-w-full max-h-full"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  const parent = e.currentTarget.parentElement;
                  if (parent) {
                    const placeholder = document.createElement('div');
                    placeholder.className = 'text-white font-bold text-xl';
                    placeholder.innerText = appName.charAt(0);
                    parent.appendChild(placeholder);
                  }
                }}
              />
            ) : (
              <div className="text-white font-bold text-xl uppercase">
                {appName.charAt(0)}
              </div>
            )}
          </div>

          <h1 className="text-4xl lg:text-5xl font-bold font-display tracking-tight leading-[1.1] mb-6">
            Central de Operações Executivas Integrada
          </h1>

          <p className="text-lg text-gray-400 mb-10 max-w-lg leading-relaxed">
            O {appName} é um ERP moderno e seguro para gerenciar empresas, projetos, produtos e finanças corporativas.
            Uma plataforma unificada projetada para simplificar decisões executivas.
          </p>

          <div className="flex flex-col gap-6">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                <CheckCircle2 size={20} className="text-emerald-400" />
              </div>
              <div>
                <h3 className="font-bold text-white text-sm">Gestão Estratégica Corporativa</h3>
                <p className="text-gray-400 text-sm">Controle seus projetos, empresas e finanças em uma visão unificada.</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                <ShieldCheck size={20} className="text-blue-400" />
              </div>
              <div>
                <h3 className="font-bold text-white text-sm">Segurança Enterprise de Criptografia</h3>
                <p className="text-gray-400 text-sm">Segurança corporativa avançada com validação e autenticação de duplo fator.</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                <Sparkles size={20} className="text-indigo-400" />
              </div>
              <div>
                <h3 className="font-bold text-white text-sm">Automação com Inteligência Artificial</h3>
                <p className="text-gray-400 text-sm">Aproveite IA para estruturar ideias, planos de negócios e documentos.</p>
              </div>
            </div>
          </div>
        </div>

        <div className="relative z-10 mt-20">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-widest">
            © {new Date().getFullYear()} {appName.toUpperCase()} SYSTEMS
          </p>
        </div>
      </div>

      {/* Right Side - Login / Register Form */}
      <div className="flex-1 flex items-center justify-center p-4 sm:p-6 bg-[#FAFAFA]">
        <div className="bg-[#FFFFFF] p-6 sm:p-10 rounded-[30px] border border-[#0F172A0F] shadow-[0_8px_40px_rgba(0,0,0,0.04)] w-full max-w-[480px] flex flex-col items-center relative overflow-hidden my-6">
          {/* Subtle Accent Line */}
          <div className="absolute top-0 w-full h-1 bg-gradient-to-r from-transparent via-[#111111]/30 to-transparent"></div>

          {/* Mobile Icon */}
          <div className="md:hidden w-14 h-14 rounded-2xl bg-[#111111] flex items-center justify-center mb-6 shadow-[0_4px_12px_rgba(0,0,0,0.15)] overflow-hidden">
            {brandImageUrl ? (
              <img
                src={brandImageUrl}
                alt={appName}
                style={{ maxWidth: `${logoUrl ? logoSize : iconSize}px`, maxHeight: `${logoUrl ? logoSize : iconSize}px` }}
                className="object-contain max-w-full max-h-full"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  const parent = e.currentTarget.parentElement;
                  if (parent) {
                    const placeholder = document.createElement('div');
                    placeholder.className = 'text-white font-bold text-xl';
                    placeholder.innerText = appName.charAt(0);
                    parent.appendChild(placeholder);
                  }
                }}
              />
            ) : (
              <div className="text-white font-bold text-xl uppercase">
                {appName.charAt(0)}
              </div>
            )}
          </div>

          <div className="text-center mb-6 w-full flex flex-col items-center">
            <h2 className="text-2xl sm:text-3xl font-display font-bold text-[#111111] tracking-tight mb-1">
              {isSignUp ? 'Criar Conta Corporativa' : 'Bem-vindo de Volta'}
            </h2>
            <p className="text-[#64748B] text-xs sm:text-sm max-w-[340px] leading-relaxed">
              {isSignUp
                ? 'Cadastre seu perfil de acesso corporativo com segurança.'
                : 'Acesse sua plataforma unificada de gestão executiva.'}
            </p>
          </div>

          <div className="w-full relative">
            <AnimatePresence mode="wait">
              <motion.div
                key={isSignUp ? 'signup-form' : 'login-form'}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.2 }}
                className="w-full"
              >
                {/* Cyzor Stylized Error Alert Banner */}
                {serverError && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="w-full bg-red-50/90 border border-red-200/80 text-red-800 p-4 rounded-[20px] mb-6 shadow-sm relative overflow-hidden"
                  >
                    <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-red-500"></div>
                    <div className="flex items-start gap-3 pl-1 pr-6">
                      <div className="p-1 bg-red-100 rounded-lg text-red-600 shrink-0 mt-0.5">
                        <AlertTriangle size={16} />
                      </div>
                      <div className="flex-1">
                        <h4 className="text-xs font-bold text-red-900 tracking-wide uppercase mb-0.5">
                          {isSignUp ? 'Falha no Cadastro' : 'Atenção na Autenticação'}
                        </h4>
                        <p className="text-xs text-red-700 leading-relaxed font-medium">
                          {serverError}
                        </p>
                        {isSignUp && (serverError.includes('cadastrado') || serverError.includes('já existe')) && (
                          <button
                            type="button"
                            onClick={() => {
                              const typedEmail = registerForm.getValues('email');
                              if (typedEmail) {
                                loginForm.setValue('email', typedEmail);
                              }
                              setIsSignUp(false);
                              setServerError('');
                            }}
                            className="mt-2.5 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-red-100 hover:bg-red-200 text-red-900 text-xs font-bold transition-colors cursor-pointer"
                          >
                            Ir para tela de Login
                            <ArrowRight size={14} />
                          </button>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => setServerError('')}
                        className="text-red-400 hover:text-red-700 transition-colors absolute top-3.5 right-3"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  </motion.div>
                )}

                {isSignUp ? (
                  /* REGISTER FORM WITH ADVANCED ENTERPRISE VALIDATIONS */
                  <form
                    onSubmit={registerForm.handleSubmit(onRegisterSubmit)}
                    className="w-full flex flex-col gap-4"
                    noValidate
                  >
                    {/* Full Name Field */}
                    <div className="flex flex-col gap-1.5 w-full">
                      <div className="flex justify-between items-center px-1">
                        <label className="text-[11px] font-bold tracking-widest uppercase text-[#64748B]">
                          NOME COMPLETO *
                        </label>
                        {registerForm.formState.dirtyFields.fullName && !registerForm.formState.errors.fullName && (
                          <span className="text-[10px] text-emerald-600 font-bold flex items-center gap-1">
                            <Check size={12} /> Válido
                          </span>
                        )}
                      </div>
                      <div className="relative group">
                        <User
                          className={`absolute left-4 top-1/2 -translate-y-1/2 transition-colors ${
                            registerForm.formState.errors.fullName
                              ? 'text-red-500'
                              : 'text-[#64748B] group-focus-within:text-[#111111]'
                          }`}
                          size={18}
                          strokeWidth={2.5}
                        />
                        <input
                          type="text"
                          placeholder="Ex: Carlos Executive da Silva"
                          {...registerForm.register('fullName')}
                          className={`w-full bg-[#FFFFFF] border rounded-[16px] py-3.5 pl-12 pr-4 outline-none transition-all text-[#111111] text-sm font-medium placeholder:text-[#64748B]/50 shadow-sm ${
                            registerForm.formState.errors.fullName
                              ? 'border-red-300 focus:border-red-500 bg-red-50/10'
                              : 'border-[#0F172A0F] focus:border-[#111111]/40 hover:border-[#0F172A0F]-dark'
                          }`}
                        />
                      </div>
                      {registerForm.formState.errors.fullName && (
                        <p className="text-[11px] text-red-500 font-medium px-1 flex items-center gap-1">
                          <AlertTriangle size={12} className="shrink-0" />
                          {registerForm.formState.errors.fullName.message}
                        </p>
                      )}
                    </div>

                    {/* Organization Name Field */}
                    <div className="flex flex-col gap-1.5 w-full">
                      <div className="flex justify-between items-center px-1">
                        <label className="text-[11px] font-bold tracking-widest uppercase text-[#64748B]">
                          NOME DA ORGANIZAÇÃO / EMPRESA *
                        </label>
                        {registerForm.formState.dirtyFields.organizationName && !registerForm.formState.errors.organizationName && (
                          <span className="text-[10px] text-emerald-600 font-bold flex items-center gap-1">
                            <Check size={12} /> Válido
                          </span>
                        )}
                      </div>
                      <div className="relative group">
                        <Building
                          className={`absolute left-4 top-1/2 -translate-y-1/2 transition-colors ${
                            registerForm.formState.errors.organizationName
                              ? 'text-red-500'
                              : 'text-[#64748B] group-focus-within:text-[#111111]'
                          }`}
                          size={18}
                          strokeWidth={2.5}
                        />
                        <input
                          type="text"
                          placeholder="Ex: Cyzor Tech Ltda"
                          {...registerForm.register('organizationName')}
                          className={`w-full bg-[#FFFFFF] border rounded-[16px] py-3.5 pl-12 pr-4 outline-none transition-all text-[#111111] text-sm font-medium placeholder:text-[#64748B]/50 shadow-sm ${
                            registerForm.formState.errors.organizationName
                              ? 'border-red-300 focus:border-red-500 bg-red-50/10'
                              : 'border-[#0F172A0F] focus:border-[#111111]/40 hover:border-[#0F172A0F]-dark'
                          }`}
                        />
                      </div>
                      {registerForm.formState.errors.organizationName && (
                        <p className="text-[11px] text-red-500 font-medium px-1 flex items-center gap-1">
                          <AlertTriangle size={12} className="shrink-0" />
                          {registerForm.formState.errors.organizationName.message}
                        </p>
                      )}
                    </div>

                    {/* Corporate Email Field */}
                    <div className="flex flex-col gap-1.5 w-full">
                      <div className="flex justify-between items-center px-1">
                        <label className="text-[11px] font-bold tracking-widest uppercase text-[#64748B]">
                          E-MAIL CORPORATIVO *
                        </label>
                        {registerForm.formState.dirtyFields.email && !registerForm.formState.errors.email && (
                          <span className="text-[10px] text-emerald-600 font-bold flex items-center gap-1">
                            <Check size={12} /> Válido
                          </span>
                        )}
                      </div>
                      <div className="relative group">
                        <Mail
                          className={`absolute left-4 top-1/2 -translate-y-1/2 transition-colors ${
                            registerForm.formState.errors.email
                              ? 'text-red-500'
                              : 'text-[#64748B] group-focus-within:text-[#111111]'
                          }`}
                          size={18}
                          strokeWidth={2.5}
                        />
                        <input
                          type="email"
                          placeholder="nome@suaempresa.com"
                          {...registerForm.register('email')}
                          className={`w-full bg-[#FFFFFF] border rounded-[16px] py-3.5 pl-12 pr-4 outline-none transition-all text-[#111111] text-sm font-medium placeholder:text-[#64748B]/50 shadow-sm ${
                            registerForm.formState.errors.email
                              ? 'border-red-300 focus:border-red-500 bg-red-50/10'
                              : 'border-[#0F172A0F] focus:border-[#111111]/40 hover:border-[#0F172A0F]-dark'
                          }`}
                        />
                      </div>
                      {registerForm.formState.errors.email && (
                        <p className="text-[11px] text-red-500 font-medium px-1 flex items-center gap-1">
                          <AlertTriangle size={12} className="shrink-0" />
                          {registerForm.formState.errors.email.message}
                        </p>
                      )}
                    </div>

                    {/* Password Field */}
                    <div className="flex flex-col gap-1.5 w-full">
                      <div className="flex justify-between items-center px-1">
                        <label className="text-[11px] font-bold tracking-widest uppercase text-[#64748B]">
                          SENHA SEGUIR *
                        </label>
                        {regPasswordValue && (
                          <span className={`text-[10px] font-bold ${strength.textColor}`}>
                            Força: {strength.text}
                          </span>
                        )}
                      </div>
                      <div className="relative group">
                        <Lock
                          className={`absolute left-4 top-1/2 -translate-y-1/2 transition-colors ${
                            registerForm.formState.errors.password
                              ? 'text-red-500'
                              : 'text-[#64748B] group-focus-within:text-[#111111]'
                          }`}
                          size={18}
                          strokeWidth={2.5}
                        />
                        <input
                          type={showPassword ? 'text' : 'password'}
                          placeholder="••••••••"
                          {...registerForm.register('password')}
                          className={`w-full bg-[#FFFFFF] border rounded-[16px] py-3.5 pl-12 pr-12 outline-none transition-all text-[#111111] text-sm font-medium placeholder:text-[#64748B]/50 shadow-sm ${
                            registerForm.formState.errors.password
                              ? 'border-red-300 focus:border-red-500 bg-red-50/10'
                              : 'border-[#0F172A0F] focus:border-[#111111]/40 hover:border-[#0F172A0F]-dark'
                          }`}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-[#111111] transition-colors p-1"
                        >
                          {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>

                      {/* Password Strength Meter & Live Checklist */}
                      {regPasswordValue && (
                        <div className="p-3 bg-neutral-50 rounded-[14px] border border-neutral-100 flex flex-col gap-2 mt-1">
                          <div className="flex gap-1.5 w-full h-1.5">
                            {[1, 2, 3, 4].map((step) => (
                              <div
                                key={step}
                                className={`flex-1 rounded-full transition-all duration-300 ${
                                  passedCount >= step ? strength.color : 'bg-neutral-200'
                                }`}
                              ></div>
                            ))}
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 mt-1">
                            {pwdReqs.map((req, idx) => (
                              <div key={idx} className="flex items-center gap-1.5 text-[10px]">
                                {req.pass ? (
                                  <Check size={12} className="text-emerald-600 font-bold shrink-0" />
                                ) : (
                                  <div className="w-1.5 h-1.5 rounded-full bg-neutral-300 shrink-0 ml-1"></div>
                                )}
                                <span className={req.pass ? 'text-emerald-700 font-medium' : 'text-neutral-500'}>
                                  {req.label}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {registerForm.formState.errors.password && (
                        <p className="text-[11px] text-red-500 font-medium px-1 flex items-center gap-1">
                          <AlertTriangle size={12} className="shrink-0" />
                          {registerForm.formState.errors.password.message}
                        </p>
                      )}
                    </div>

                    {/* Confirm Password Field */}
                    <div className="flex flex-col gap-1.5 w-full">
                      <div className="flex justify-between items-center px-1">
                        <label className="text-[11px] font-bold tracking-widest uppercase text-[#64748B]">
                          CONFIRMAR SENHA *
                        </label>
                        {registerForm.formState.dirtyFields.confirmPassword && !registerForm.formState.errors.confirmPassword && (
                          <span className="text-[10px] text-emerald-600 font-bold flex items-center gap-1">
                            <Check size={12} /> Coincide
                          </span>
                        )}
                      </div>
                      <div className="relative group">
                        <Lock
                          className={`absolute left-4 top-1/2 -translate-y-1/2 transition-colors ${
                            registerForm.formState.errors.confirmPassword
                              ? 'text-red-500'
                              : 'text-[#64748B] group-focus-within:text-[#111111]'
                          }`}
                          size={18}
                          strokeWidth={2.5}
                        />
                        <input
                          type={showConfirmPassword ? 'text' : 'password'}
                          placeholder="••••••••"
                          {...registerForm.register('confirmPassword')}
                          className={`w-full bg-[#FFFFFF] border rounded-[16px] py-3.5 pl-12 pr-12 outline-none transition-all text-[#111111] text-sm font-medium placeholder:text-[#64748B]/50 shadow-sm ${
                            registerForm.formState.errors.confirmPassword
                              ? 'border-red-300 focus:border-red-500 bg-red-50/10'
                              : 'border-[#0F172A0F] focus:border-[#111111]/40 hover:border-[#0F172A0F]-dark'
                          }`}
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-[#111111] transition-colors p-1"
                        >
                          {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                      {registerForm.formState.errors.confirmPassword && (
                        <p className="text-[11px] text-red-500 font-medium px-1 flex items-center gap-1">
                          <AlertTriangle size={12} className="shrink-0" />
                          {registerForm.formState.errors.confirmPassword.message}
                        </p>
                      )}
                    </div>

                    {/* Terms and Privacy Checkbox */}
                    <div className="flex flex-col gap-1 w-full pt-1">
                      <label className="flex items-start gap-2.5 cursor-pointer group">
                        <input
                          type="checkbox"
                          {...registerForm.register('acceptTerms')}
                          className="mt-1 w-4 h-4 rounded border-neutral-300 text-[#111111] focus:ring-[#111111] cursor-pointer"
                        />
                        <span className="text-xs text-[#64748B] leading-normal select-none">
                          Li e concordo com os{' '}
                          <button
                            type="button"
                            onClick={() => onNavigate && onNavigate('terms')}
                            className="text-[#111111] font-bold hover:underline"
                          >
                            Termos de Uso
                          </button>{' '}
                          e a{' '}
                          <button
                            type="button"
                            onClick={() => onNavigate && onNavigate('privacy')}
                            className="text-[#111111] font-bold hover:underline"
                          >
                            Política de Privacidade
                          </button>{' '}
                          da {appName} Systems.
                        </span>
                      </label>
                      {registerForm.formState.errors.acceptTerms && (
                        <p className="text-[11px] text-red-500 font-medium px-1 flex items-center gap-1">
                          <AlertTriangle size={12} className="shrink-0" />
                          {registerForm.formState.errors.acceptTerms.message}
                        </p>
                      )}
                    </div>

                    <button
                      type="submit"
                      disabled={isLoading}
                      className="w-full bg-[#111111] text-[#FFFFFF] py-4 rounded-[16px] font-bold mt-3 hover:bg-black transition-all shadow-[0_4px_14px_rgba(0,0,0,0.15)] hover:shadow-[0_6px_20px_rgba(0,0,0,0.2)] flex items-center justify-center gap-2 disabled:bg-[#111111]/60"
                    >
                      {isLoading ? (
                        <span className="flex items-center gap-2">
                          <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                          Criando Conta Corporativa...
                        </span>
                      ) : (
                        'Criar Conta Corporativa'
                      )}
                    </button>
                  </form>
                ) : (
                  /* LOGIN FORM WITH ENTERPRISE VALIDATION */
                  <form
                    onSubmit={loginForm.handleSubmit(onLoginSubmit)}
                    className="w-full flex flex-col gap-4"
                    noValidate
                  >
                    {/* Email Field */}
                    <div className="flex flex-col gap-1.5 w-full">
                      <div className="flex justify-between items-center px-1">
                        <label className="text-[11px] font-bold tracking-widest uppercase text-[#64748B]">
                          E-MAIL CORPORATIVO
                        </label>
                        {loginForm.formState.dirtyFields.email && !loginForm.formState.errors.email && (
                          <span className="text-[10px] text-emerald-600 font-bold flex items-center gap-1">
                            <Check size={12} /> Válido
                          </span>
                        )}
                      </div>
                      <div className="relative group">
                        <Mail
                          className={`absolute left-4 top-1/2 -translate-y-1/2 transition-colors ${
                            loginForm.formState.errors.email
                              ? 'text-red-500'
                              : 'text-[#64748B] group-focus-within:text-[#111111]'
                          }`}
                          size={18}
                          strokeWidth={2.5}
                        />
                        <input
                          type="email"
                          placeholder="nome@cyzor.com"
                          {...loginForm.register('email')}
                          className={`w-full bg-[#FFFFFF] border rounded-[16px] py-3.5 pl-12 pr-4 outline-none transition-all text-[#111111] text-sm font-medium placeholder:text-[#64748B]/50 shadow-sm ${
                            loginForm.formState.errors.email
                              ? 'border-red-300 focus:border-red-500 bg-red-50/10'
                              : 'border-[#0F172A0F] focus:border-[#111111]/40 hover:border-[#0F172A0F]-dark'
                          }`}
                        />
                      </div>
                      {loginForm.formState.errors.email && (
                        <p className="text-[11px] text-red-500 font-medium px-1 flex items-center gap-1">
                          <AlertTriangle size={12} className="shrink-0" />
                          {loginForm.formState.errors.email.message}
                        </p>
                      )}
                    </div>

                    {/* Password Field */}
                    <div className="flex flex-col gap-1.5 w-full">
                      <div className="flex justify-between items-center px-1">
                        <label className="text-[11px] font-bold tracking-widest uppercase text-[#64748B]">
                          SENHA DE ACESSO
                        </label>
                      </div>
                      <div className="relative group">
                        <Lock
                          className={`absolute left-4 top-1/2 -translate-y-1/2 transition-colors ${
                            loginForm.formState.errors.password
                              ? 'text-red-500'
                              : 'text-[#64748B] group-focus-within:text-[#111111]'
                          }`}
                          size={18}
                          strokeWidth={2.5}
                        />
                        <input
                          type={showPassword ? 'text' : 'password'}
                          placeholder="••••••••"
                          {...loginForm.register('password')}
                          className={`w-full bg-[#FFFFFF] border rounded-[16px] py-3.5 pl-12 pr-12 outline-none transition-all text-[#111111] text-sm font-medium placeholder:text-[#64748B]/50 shadow-sm ${
                            loginForm.formState.errors.password
                              ? 'border-red-300 focus:border-red-500 bg-red-50/10'
                              : 'border-[#0F172A0F] focus:border-[#111111]/40 hover:border-[#0F172A0F]-dark'
                          }`}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-[#111111] transition-colors p-1"
                        >
                          {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                      {loginForm.formState.errors.password && (
                        <p className="text-[11px] text-red-500 font-medium px-1 flex items-center gap-1">
                          <AlertTriangle size={12} className="shrink-0" />
                          {loginForm.formState.errors.password.message}
                        </p>
                      )}
                    </div>

                    <button
                      type="submit"
                      disabled={isLoading}
                      className="w-full bg-[#111111] text-[#FFFFFF] py-4 rounded-[16px] font-bold mt-2 hover:bg-black transition-all shadow-[0_4px_14px_rgba(0,0,0,0.15)] hover:shadow-[0_6px_20px_rgba(0,0,0,0.2)] flex items-center justify-center gap-2 disabled:bg-[#111111]/60"
                    >
                      {isLoading ? (
                        <span className="flex items-center gap-2">
                          <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                          Autenticando na Nuvem...
                        </span>
                      ) : (
                        'Acessar Plataforma'
                      )}
                    </button>
                  </form>
                )}

                <div className="w-full flex items-center my-6 gap-3">
                  <div className="flex-1 h-[1px] bg-[#0F172A0F]"></div>
                  <span className="text-[10px] font-bold uppercase tracking-widest text-[#64748B]">
                    OU ACESSE VIA GOOGLE CLOUD
                  </span>
                  <div className="flex-1 h-[1px] bg-[#0F172A0F]"></div>
                </div>

                <button
                  onClick={loginWithGoogle}
                  disabled={isLoading}
                  type="button"
                  className="w-full bg-white hover:bg-[#FAFAFA] text-[#111111] border border-[#0F172A0F] py-3.5 rounded-[16px] font-bold flex items-center justify-center gap-2.5 transition-all shadow-[0_2px_8px_rgba(0,0,0,0.01)]"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      fill="#4285F4"
                    />
                    <path
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      fill="#34A853"
                    />
                    <path
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l3.66-2.85z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.85c.87-2.6 3.3-4.53 6.16-4.53z"
                      fill="#EA4335"
                    />
                  </svg>
                  Google Cloud SSO Executive
                </button>
              </motion.div>
            </AnimatePresence>
          </div>

          <button
            onClick={handleToggleMode}
            className="text-xs font-bold text-[#64748B] hover:text-[#111111] mt-6 hover:underline transition-colors block relative z-10"
          >
            {isSignUp ? 'Já possui uma conta corporativa? Inicie sessão' : 'Novo na plataforma? Registre sua organização'}
          </button>

          <div className="mt-6 flex items-center justify-center gap-4 text-[10px] font-bold uppercase tracking-widest text-neutral-400 relative z-10">
            <button
              onClick={() => onNavigate && onNavigate('privacy')}
              className="hover:text-neutral-800 transition-colors"
            >
              Privacidade
            </button>
            <span>•</span>
            <button
              onClick={() => onNavigate && onNavigate('terms')}
              className="hover:text-neutral-800 transition-colors"
            >
              Termos
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

